import { useState, useMemo, useEffect } from 'react'
import {
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  ListItemIcon,
  Paper,
  Tab,
  Typography,
  Button,
  Stack
} from '@mui/material'
import { TabPanel, TabList, TabContext } from '@mui/lab'
import { NavLink } from 'react-router-dom'
import { ArrowForwardIosOutlined, ChevronRight, ChevronLeft } from '@mui/icons-material'
import dayjs from 'dayjs'
import weekday from 'dayjs/plugin/weekday'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'

import { useProtocolsList, Protocol } from '@services/protocols/protocols.hook'
import { PROTOCOL_TYPE } from '@services/protocols/protocols.consts'
import { formatFlatNumber } from '@services/protocols/protocols.helpers'
import { protocolsDate, leaseDate, getProtocolFetchRange } from '@services/date/date.models'
import { useAtom, useSetAtom } from 'jotai'
import { useAllProtocolsOfflineStatus } from '../hooks/useProtocolOfflineStatus'
import ProtocolOfflineIndicator from './ProtocolOfflineIndicator'

import 'dayjs/locale/pl'

dayjs.extend(isSameOrAfter)
dayjs.extend(isSameOrBefore)
dayjs.extend(weekday)

function SelectProtocolByDay() {
  const [date, setDate] = useAtom(protocolsDate)
  const setLeaseDate = useSetAtom(leaseDate)
  const [value, setValue] = useState('acceptance')
  const visibleWeek = useMemo(
    () => ({
      start: dayjs(date).locale('pl').weekday(0),
      end: dayjs(date).locale('pl').weekday(6)
    }),
    [date]
  )

  // Keep API date window in sync with the visible week (weeks can span months;
  // Prev/Next only used to update protocolsDate and left leaseDate on the old month).
  useEffect(() => {
    setLeaseDate(getProtocolFetchRange(date))
  }, [date, setLeaseDate])

  const handleChange = (_: React.SyntheticEvent, newValue: string) => {
    setValue(newValue)
  }

  const { protocolsList } = useProtocolsList()

  const protocols = protocolsList
    .filter(({ leaseStartDate, effectiveLeaseEnd, protocolType }) => {
      if (protocolType === PROTOCOL_TYPE.acceptance) {
        return !!leaseStartDate
      }
      return !!effectiveLeaseEnd
    })
    .filter(({ leaseStartDate, effectiveLeaseEnd, protocolType }) => {
      if (protocolType === PROTOCOL_TYPE.acceptance) {
        return (
          visibleWeek.start.isSameOrBefore(leaseStartDate, 'day') &&
          visibleWeek.end.isSameOrAfter(leaseStartDate, 'day')
        )
      }
      return (
        visibleWeek.start.isSameOrBefore(effectiveLeaseEnd, 'day') &&
        visibleWeek.end.isSameOrAfter(effectiveLeaseEnd, 'day')
      )
    })
    .reduce(
      (acc, curr) => {
        if (curr.protocolType === PROTOCOL_TYPE.acceptance) {
          return {
            acceptance: [...acc.acceptance, curr],
            delivery: acc.delivery
          }
        }
        return {
          delivery: [...acc.delivery, curr],
          acceptance: acc.acceptance
        }
      },
      {
        acceptance: [] as Protocol[],
        delivery: [] as Protocol[]
      }
    )

  // Get offline status for all visible protocols
  const allProtocolIds = useMemo(() => {
    try {
      return [...protocols.acceptance, ...protocols.delivery]
        .map((p) => p.id)
        .filter((id) => id != null)
    } catch (error) {
      console.error('Error extracting protocol IDs:', error)
      return []
    }
  }, [protocols.acceptance, protocols.delivery])

  const protocolsOfflineStatus = useAllProtocolsOfflineStatus(allProtocolIds)

  return (
    <Box>
      <Stack
        direction='row'
        spacing={2}
        sx={{
          width: '100%',
          justifyContent: 'space-between',
          mb: 2
        }}
      >
        <Button
          variant='outlined'
          startIcon={<ChevronLeft />}
          onClick={() => setDate(dayjs(date).subtract(1, 'week').toISOString())}
        >
          Poprzedni
        </Button>
        <Button
          variant='outlined'
          endIcon={<ChevronRight />}
          onClick={() => setDate(dayjs(date).add(1, 'week').toISOString())}
        >
          Następny
        </Button>
      </Stack>
      <Paper sx={{ flex: 1, width: '100%', typography: 'body1' }}>
        <Typography p={2}>
          {visibleWeek.start.format('dddd, D MMMM YYYY')} -{' '}
          {visibleWeek.end.format('dddd, D MMMM YYYY')}
        </Typography>
        <Box>
          <TabContext value={value}>
            <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
              <TabList onChange={handleChange}>
                <Tab label={`Wprowadzki (${protocols.acceptance.length})`} value='acceptance' />
                <Tab label={`Wyprowadzki (${protocols.delivery.length})`} value='delivery' />
              </TabList>
            </Box>
            <TabPanel value='acceptance' sx={{ p: 0 }}>
              <List>
                {protocols.acceptance.map((protocol) => (
                  <ListItem key={`acceptance_${protocol.id}`}>
                    <ListItemButton
                      sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}
                      component={NavLink}
                      to={`/${protocol.id}`}
                    >
                      <Box sx={{ display: 'flex', gap: '1rem' }}>
                        <ListItemText
                          sx={{ minWidth: '300px' }}
                          primary={`${protocol.contractNumber ?? 'n/a'} (${protocol.leaseContract})`}
                        />
                        {protocol.flat && (
                          <ListItemText
                            primary={`Lokal Nr: ${formatFlatNumber(protocol.flat)}`}
                            sx={{ minWidth: '150px' }}
                          />
                        )}
                        {protocol.status && <ListItemText primary={`status: ${protocol.status}`} />}
                        <Typography
                          sx={{
                            color: '#f44336',
                            fontWeight: 500,
                            minWidth: '120px',
                            marginTop: '4px'
                          }}
                        >
                          Data. {dayjs(protocol.leaseStartDate).format('DD.MM.YYYY')}
                        </Typography>
                      </Box>
                      <Stack direction='row' alignItems='center' spacing={1}>
                        {protocolsOfflineStatus.get(protocol.id) && (
                          <ProtocolOfflineIndicator
                            status={protocolsOfflineStatus.get(protocol.id)!}
                            compact
                          />
                        )}
                        <ListItemIcon>
                          <ArrowForwardIosOutlined fontSize='small' />
                        </ListItemIcon>
                      </Stack>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              {!protocols.acceptance.length && (
                <Typography p={4} variant='body2'>
                  Brak protokolów
                </Typography>
              )}
            </TabPanel>
            <TabPanel value='delivery' sx={{ p: 0 }}>
              <List>
                {protocols.delivery.map((protocol) => (
                  <ListItem key={`delivery_${protocol.id}`}>
                    <ListItemButton
                      sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}
                      component={NavLink}
                      to={`/${protocol.id}`}
                    >
                      <Box sx={{ display: 'flex', gap: '2rem' }}>
                        <ListItemText
                          primary={`${protocol.contractNumber ?? 'n/a'} (${protocol.leaseContract})`}
                        />
                        {protocol.flat && (
                          <ListItemText primary={`Lokal Nr: ${formatFlatNumber(protocol.flat)}`} />
                        )}
                        {protocol.status && <ListItemText primary={`status: ${protocol.status}`} />}
                        <Typography
                          sx={{
                            color: '#f44336',
                            fontWeight: 500,
                            minWidth: '120px',
                            marginTop: '4px'
                          }}
                        >
                          Data. {dayjs(protocol.effectiveLeaseEnd).format('DD.MM.YYYY')}
                        </Typography>
                      </Box>
                      <Stack direction='row' alignItems='center' spacing={1}>
                        {protocolsOfflineStatus.get(protocol.id) && (
                          <ProtocolOfflineIndicator
                            status={protocolsOfflineStatus.get(protocol.id)!}
                            compact
                          />
                        )}
                        <ListItemIcon>
                          <ArrowForwardIosOutlined fontSize='small' />
                        </ListItemIcon>
                      </Stack>
                    </ListItemButton>
                  </ListItem>
                ))}
              </List>
              {!protocols.delivery.length && (
                <Typography p={4} variant='body2'>
                  Brak protokolów
                </Typography>
              )}
            </TabPanel>
          </TabContext>
        </Box>
      </Paper>
    </Box>
  )
}

export default SelectProtocolByDay
